import { useState } from "react";
import { Star, TrendUp, TrendDown, ForkKnife, UsersFour, Broom, Armchair, CurrencyDollar } from "@phosphor-icons/react";
import useSiteContent from "../../lib/useSiteContent";
import { TrendPill, ACCENT, dT } from "./CsDashboard";

const QUESTION_ICON_RULES = [
  { test: /food|dish|menu|taste/i, Icon: ForkKnife, bg: "bg-amber-50", color: "#D97706" },
  { test: /service|staff|waiter|server/i, Icon: UsersFour, bg: "bg-blue-50", color: "#2563EB" },
  { test: /clean/i, Icon: Broom, bg: "bg-violet-50", color: "#7C3AED" },
  { test: /atmosphere|ambiance|ambience|decor/i, Icon: Armchair, bg: "bg-purple-50", color: "#9333EA" },
  { test: /value|price|money|cost/i, Icon: CurrencyDollar, bg: "bg-yellow-50", color: "#CA8A04" },
];
export function questionIconMeta(question = "") {
  const hit = QUESTION_ICON_RULES.find((r) => r.test.test(question));
  return hit || { Icon: Star, bg: "bg-emerald-50", color: ACCENT };
}

function RatingCard({ q, testid, retired }) {
  const { Icon, bg, color } = questionIconMeta(q.question);
  return (
    <div className={`bg-white rounded-lg border shadow-sm p-4 ${retired ? "border-dashed border-slate-300 opacity-70" : "border-slate-200"}`} data-testid={`${testid}-card-${q.question_id}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-3 ${bg}`}><Icon size={16} weight="fill" style={{ color }} /></div>
      <div className="text-xs text-slate-500 leading-snug mb-1.5 min-h-[2rem]">{q.question}</div>
      <div className="text-2xl font-bold tracking-tighter text-slate-900">{q.avg}</div>
      {retired ? <div className="mt-1.5 text-[10px] font-semibold uppercase text-slate-400">Retired</div> : <div className="mt-1.5"><TrendPill value={q.delta} /></div>}
    </div>
  );
}

export function RatingsSummaryGrid({ rows, testid = "cs-ratings-summary" }) {
  const [showRetired, setShowRetired] = useState(false);
  const content = useSiteContent();
  const active = rows.filter((q) => q.active !== false);
  const retired = rows.filter((q) => q.active === false);
  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {active.map((q) => <RatingCard key={q.question_id} q={q} testid={testid} />)}
        {active.length === 0 && <div className="text-sm text-slate-400 col-span-6">{dT(content, "cs_dash_ratings_empty", "No ratings data yet.")}</div>}
      </div>
      {retired.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowRetired(!showRetired)} data-testid={`${testid}-toggle-retired`} className="text-xs font-semibold text-slate-500 hover:text-slate-800 underline">
            {showRetired ? "Hide" : "View"} retired questions ({retired.length})
          </button>
          {showRetired && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-3">
              {retired.map((q) => <RatingCard key={q.question_id} q={q} testid={testid} retired />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function RankedList({ title, rows, tone, testid }) {
  const content = useSiteContent();
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4" data-testid={testid}>
      <div className="text-sm font-medium text-slate-700 mb-3">{title}</div>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.question_id} className="flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-1.5 text-slate-600">
              {tone === "up" ? <TrendUp size={13} className="text-emerald-500 shrink-0" /> : <TrendDown size={13} className="text-rose-500 shrink-0" />}
              {r.question}
            </span>
            <span className="flex items-center gap-1 font-bold text-slate-900 shrink-0"><Star size={12} weight="fill" style={{ color: ACCENT }} />{r.avg}</span>
          </div>
        ))}
        {rows.length === 0 && <div className="text-sm text-slate-400">{dT(content, "cs_dash_ranked_empty", "Not enough data yet.")}</div>}
      </div>
    </div>
  );
}

export function CsDashboardRatings({ data }) {
  const content = useSiteContent();
  // A business can now have MULTIPLE feedback templates (different QR
  // codes asking different questions — e.g. a training company running
  // "Excel Training" vs "React Training" sessions). When more than one is
  // actually in play across the current view, split the Ratings Summary
  // into one labeled section per template instead of one flat, potentially
  // confusing grid (same-looking question text from unrelated templates
  // sitting side by side with no context). With just one template (the
  // vast majority of businesses), this collapses back to exactly the
  // single flat grid shown before this feature existed.
  const templates = data.templates || [];
  const grouped = templates.length > 1;
  return (
    <div className="space-y-5">
      {grouped ? (
        <div className="space-y-6" data-testid="cs-ratings-grouped-by-template">
          {templates.map((t) => {
            const rows = (data.ratings_by_question || []).filter((r) => r.template_id === t.id);
            if (rows.length === 0) return null;
            return (
              <div key={t.id} data-testid={`cs-ratings-template-group-${t.id}`}>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <div className="text-sm font-semibold text-slate-800">{t.name}</div>
                  {t.locations?.length > 0 && (
                    <div className="text-xs text-slate-400">· {t.locations.join(", ")}</div>
                  )}
                </div>
                <RatingsSummaryGrid rows={rows} testid={`cs-ratings-by-question-${t.id}`} />
              </div>
            );
          })}
        </div>
      ) : (
        <RatingsSummaryGrid rows={data.ratings_by_question} testid="cs-ratings-by-question" />
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <RankedList title={dT(content, "cs_dash_top_strengths_title", "Top Strengths")} rows={data.top_strengths} tone="up" testid="cs-top-strengths" />
        <RankedList title={dT(content, "cs_dash_top_opportunities_title", "Top Opportunities")} rows={data.top_opportunities} tone="down" testid="cs-top-opportunities" />
      </div>
    </div>
  );
}
